// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import classNames from '@ta/common.classnames';
import * as Import from '@ta/platform.import';
import type { GeoPillFlyoutProps } from '@ta/brand.trip-search-geopill-flyout';
import type { NavLinkType, TabType } from '../types';
import styles from './MenuTab.less';
import TopLevelNavLink from './TopLevelNavLink';

const GeoPillFlyout = Import.component.onUsage<_, GeoPillFlyoutProps>(
  () => import('@ta/brand.trip-search-geopill-flyout'),
  'GeoPillFlyout',
);

type ExternalProps = {|
  /** Show the trigger as active. E.g., currently on the page relevant to this menu tab. */
  active?: boolean,

  /**
   * Show the trigger as hovering.
   * This is a prop because we need to show the trigger as "hovering" when hovering over the menu contents, too.
   */
  hovering?: boolean,

  /** Ref target function for setting an element as the hover target. Please see HoverChain.jsx if you're curious about this. */
  setHoverTarget?: () => void,

  /** Whether this tab is a link or not */
  tabType?: TabType,

  /** Data model for the link, if TabType === link */
  link?: NavLinkType,

  /** Data model for the tab contents, if TabType === nonlink. E.g., for the ellipsis menu which is a span */
  nonLinkContents?: React.Node,
|};

type Props = {|
  ...ExternalProps,
  /** Whether or not this page is geoscoped, at which point the classes look different. */
  isGeoScoped: boolean,
|};

/** A tab that wraps a top-level link. */
const MenuTab = (props: Props) => {
  // styles.menuTab and styles.hovering do exist, but they're prefixed with :global(.ui_tab) which is probably why this rule is confused.
  // eslint-disable-next-line css-modules/no-undef-class
  const classes = classNames('ui_tab', styles.menuTab, {
    // eslint-disable-next-line css-modules/no-undef-class
    [styles.hovering]: props.hovering,
    active: props.active,
  });

  const willOpenGeopill =
    (!!props.link?.urlWithGeoTemplate || !!props.link?.urlWithLatLngTemplate) && !props.isGeoScoped;

  if (props.tabType === 'link' && props.link) {
    if (willOpenGeopill) {
      return (
        <GeoPillFlyout
          latLngUrlTemplate={props.link.urlWithLatLngTemplate}
          geoUrlTemplate={props.link.urlWithGeoTemplate}
          trackingLabel={props.link.trackingLabel}
        >
          {setTarget =>
            props.link ? (
              <TopLevelNavLink
                link={props.link}
                setTarget={setTarget}
                preventDefault={willOpenGeopill}
                extraClasses={classes}
                nonLink={props.link.id === 'tourism' || props.link.id === 'scoped-home'}
              />
            ) : null
          }
        </GeoPillFlyout>
      );
    }

    return (
      <TopLevelNavLink
        link={props.link}
        setTarget={props.setHoverTarget}
        extraClasses={classes}
        nonLink={props.link?.id === 'tourism' || props.link?.id === 'scoped-home'}
      />
    );
  } else if (props.tabType === 'nonlink' && props.nonLinkContents) {
    return (
      <span className={classes} ref={props.setHoverTarget}>
        {props.nonLinkContents}
      </span>
    );
  }

  return null;
};

MenuTab.defaultProps = {
  active: false,
  hovering: false,
  setHoverTarget: () => {},
  tabType: 'link',
  link: undefined,
  nonLinkContents: undefined,
};

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  isGeoScoped: state.route.isNearby ? !!state.page.geoId : !!state.route.geo,
}))(MenuTab);
